<template>
  <div class="flex-center position-ref full-height">
    <div class="content">
      <Loading :loading="loading"></Loading>
      <img :src="config.logo" :alt="config.name" class="app-logo" />
      <h3 v-if="!error">{{ status }}</h3>
      <SsoError v-if="error" :message="error"></SsoError>
      <button v-if="showOpenAppButton" type="button" class="open-app-button" @click="openAppByButton">
        Uygulamayı Aç
      </button>
      <hr v-if="showStoreBadges" class="divider" />
      <p v-if="appNotOpened" class="fallback-message">
        Uygulama otomatik açılmadıysa yukarıdaki "Uygulamayı Aç" düğmesine dokunun. Yine
        açılmazsa cihazınızda {{ config.name }} uygulamasının yüklü olduğundan emin olup tekrar deneyin.
      </p>
      <div v-if="showStoreBadges" class="store-badges">
        <a v-if="config.storeLinks.appStore" :href="config.storeLinks.appStore" target="_blank" rel="noopener">
          <img src="/assets/images/app-store.png" alt="App Store'dan indirin" />
        </a>
        <a v-if="config.storeLinks.googlePlay" :href="config.storeLinks.googlePlay" target="_blank" rel="noopener">
          <img src="/assets/images/google-play.png" alt="Google Play'den indirin" />
        </a>
      </div>

      <!-- debug modu: adımlar ekrana basılır, app'e geçiş kullanıcı dokunana kadar bekletilir -->
      <div v-if="debug" class="be-debug">
        <h4>DEBUG MODE — the app is not opened automatically.</h4>
        <div v-for="(step, index) in steps" :key="index" class="be-debug-step">
          <div :class="['be-debug-title', step.ok ? 'is-ok' : 'is-fail']">
            [{{ index + 1 }}] {{ step.title }} {{ step.ok ? '✔' : '✖' }}
          </div>
          <pre v-if="step.data">{{ step.data }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from "@/components/Loading";
import SsoError from "@/components/SsoError";
import axios from "axios";
import CryptoJS from "crypto-js";

/**
 * BoostifySpeak / BoostifyWrite / BoostifyExam mobil uygulamalarına okul platformundan tek tık giriş.
 * Route'lar: /boostifyspeak (app: 'speak'), /boostifywrite (app: 'write'), /boostifyexam (app: 'exam').
 *
 *   Okul platformu → service.ueslms.com/#/boostifyspeak?sso=xxx  (bu bileşen)
 *     1. sso çözülür (BoostifyEdu.vue ile aynı anahtar — Metodbox'ın HLL/UESPortals anahtarı)
 *     2. /api/v2/boostifyedu/user/check → yoksa /user/create  (BoostifyEdu web girişiyle aynı uçlar)
 *        `app` gönderilir; ueslms yanıta BoostifyEdu'nun çözeceği şifreli `token`'ı ekler
 *        (BoostifyEduController::ssoToken — anahtar sunucuda, tarayıcıya inmez; `timestamp` içerir)
 *     3. yalnızca öğrenci: token custom URL scheme ile app'e devredilir:
 *          boostifyspeak://login?token=<token>
 *        app token'ı quizmaker-api POST /auth/sso-mobile'a götürüp oturum açar
 *        (BoostifyExam: /mobile/exam/auth/sso-mobile).
 *
 * App'e geçiş KidzwonderJr.vue ile aynı desende (Android'de Intent URL + "Uygulamayı Aç" düğmesi):
 * bkz. oradaki goApp() notu ve ueslms/.claude/notes/kidzwonder-jr-3d-entegrasyonu.md.
 *
 * Test için `?dev=1`: uygulamaların geliştirici sürümünü (scheme `<app>-dev`, Android package `.dev`) açar.
 *
 * Bkz. ueslms/.claude/notes/boostifyedu-mobil-sso.md
 */
const APPS = {
  speak: {
    name: 'BoostifySpeak',
    logo: '/assets/logos/boostifyspeak.png',
    scheme: 'boostifyspeak',
    androidPackage: 'com.ues.boostifyspeak',
    storeLinks: {
      appStore: 'https://apps.apple.com/app/id6769026672',
      googlePlay: 'https://play.google.com/store/apps/details?id=com.ues.boostifyspeak',
    },
  },
  write: {
    name: 'BoostifyWrite',
    logo: '/assets/logos/boostifywrite.png',
    scheme: 'boostifywrite',
    androidPackage: 'com.uesturkey.boostifywrite',
    storeLinks: {
      appStore: 'https://apps.apple.com/app/id6784579529',
      googlePlay: 'https://play.google.com/store/apps/details?id=com.uesturkey.boostifywrite',
    },
  },
  exam: {
    name: 'BoostifyExam',
    logo: '/assets/logos/boostifyexam.png',
    scheme: 'boostifyexam',
    androidPackage: 'com.uesturkey.boostifyexam',
    // Henüz mağazada değil; yayınlanınca eklenecek
    storeLinks: {
      appStore: null,
      googlePlay: null,
    },
  },
};

// Token'daki timestamp quizmaker-api'de 5 dk geçerli. Kullanıcı sayfada bundan uzun beklediyse
// "Uygulamayı Aç" düğmesi önce taze bir token alır.
const TOKEN_REFRESH_AFTER_MS = 4 * 60 * 1000;

export default {
  name: "BoostifyApp",
  components: {
    Loading,
    SsoError
  },
  props: {
    app: {
      type: String,
      required: true,
      validator: (value) => Object.prototype.hasOwnProperty.call(APPS, value),
    },
  },
  data() {
    return {
      loading: false,
      // service.ueslms.com'a gelen `sso` parametresini çözmek için kullanılan AES anahtarı —
      // BoostifyEdu.vue ile aynı (Metodbox'ın HLL / UESPortals anahtarı).
      secretKey: 'T6VCnkxXEx8ixtHxpteZnbm0YF9KwvLU0iBTOf1vJz1Y3fzZ',
      apiUrl: 'https://www.ueslms.com/api/v2/boostifyedu',
      // apiUrl: 'https://ueslms.test/api/v2/boostifyedu',
      status: 'Lütfen bekleyiniz, uygulamaya yönlendiriliyorsunuz.',
      error: null,
      userData: null,
      pendingToken: null,
      tokenIssuedAt: null,
      appNotOpened: false,
      appNotOpenedTimer: null,
      useDevApp: false,
      debug: false,
      steps: [],
    }
  },
  computed: {
    config() {
      return APPS[this.app];
    },
    // Hem "app açılmadı" fallback'inde hem de hata durumunda kullanıcı uygulamayı indirebilsin
    // (mağazada henüz olmayan app'te — BoostifyExam — gösterilecek link yok)
    showStoreBadges() {
      const hasStoreLink = !!(this.config.storeLinks.appStore || this.config.storeLinks.googlePlay);
      return hasStoreLink && (this.appNotOpened || !!this.error);
    },
    // Token elimize geçtiği andan itibaren (otomatik denemeyle aynı anda) gösterilir —
    // otomatik geçiş Android/tablette sessizce engellenebiliyor, dokunuş her zaman çalışır.
    showOpenAppButton() {
      return !!this.pendingToken;
    },
  },
  beforeMount() {
    const query = this.$route.query;
    this.debug = this.isTrue(query.debug);
    this.useDevApp = this.isTrue(query.dev);

    if (!query.sso) {
      this.fail('Giriş bağlantısı eksik (sso parametresi yok). Lütfen okul platformu üzerinden tekrar deneyin.');
      return;
    }

    this.loading = true;

    try {
      const decode = decodeURIComponent(atob(query.sso));
      const bytes = CryptoJS.AES.decrypt(decode, this.secretKey).toString(CryptoJS.enc.Utf8);
      this.userData = JSON.parse(bytes);
    } catch (e) {
      this.fail('Giriş bağlantısı çözümlenemedi. Lütfen okul platformu üzerinden tekrar deneyin.');
      return;
    }

    if (this.isTrue(this.userData.debug)) {
      this.debug = true;
    }
    this.log('received from your platform (decrypted sso payload)', this.userData);

    this.ueslmsCheckUser();
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    clearTimeout(this.appNotOpenedTimer);
  },
  methods: {
    isTrue(val) {
      return val === true || val === 'true' || val === 1 || val === '1';
    },

    log(title, data, ok = true) {
      if (!this.debug) return;
      this.steps.push({
        title: title,
        data: data === undefined ? null : (typeof data === 'string' ? data : JSON.stringify(data, null, 2)),
        ok: ok,
      });
    },

    fail(message) {
      this.loading = false;
      this.error = message;
      this.pendingToken = null;
      this.log('stopped', message, false);
      console.log(this.config.name + ' SSO:', message);
    },

    requestBody() {
      return { app: this.app, debug: this.debug };
    },

    // onSuccess verilirse (düğmeden token yenileme) app'e geçiş ona bırakılır
    ueslmsCheckUser(onSuccess) {
      axios.post(this.apiUrl + '/user/check', { user: this.userData, ...this.requestBody() })
          .then(response => {
            if (response.data.success) {
              this.log('UES account: existing user matched', response.data.user);
              this.route(response.data.user, response.data.token, onSuccess);
            } else if (response.data.message) {
              // success:false + message → örn. lisans/yetki reddi; create de aynı sebeple reddedilir
              this.fail(response.data.message);
            } else {
              this.ueslmsCreate();
            }
          })
          .catch(() => {
            this.fail('Sunucuya bağlanılamadı. Lütfen tekrar deneyin.');
          });
    },

    ueslmsCreate() {
      axios.post(this.apiUrl + '/user/create', { ...this.userData, ...this.requestBody() })
          .then(response => {
            if (response.data.success) {
              this.log('UES account: new user created', response.data.user);
              this.route(response.data.user, response.data.token);
            } else {
              this.fail(response.data.message || 'Kullanıcı oluşturulamadı. Lütfen tekrar deneyin.');
            }
          })
          .catch(() => {
            this.fail('Sunucuya bağlanılamadı. Lütfen tekrar deneyin.');
          });
    },

    // Rol, backend'in çözdüğü user.role_type'a göre (okul platformunun payload'ına değil).
    // Backend öğrenci olmayana `app` ile token üretmez; uygulama da öğrenci olmayanı ayrıca reddeder.
    route(user, token, onSuccess) {
      if (!user || user.role_type !== 'student') {
        this.fail(`${this.config.name} yalnızca öğrenciler içindir.`);
        return;
      }
      if (!token) {
        // Sunucuda BOOSTIFYEDU_SSO_SECRET tanımlı değilse token gelmez (ueslms log'a hata yazar)
        this.fail('Giriş anahtarı oluşturulamadı. Lütfen okulunuzla iletişime geçiniz.');
        return;
      }

      this.pendingToken = token;
      this.tokenIssuedAt = Date.now();

      if (onSuccess) {
        onSuccess();
        return;
      }

      if (this.debug) {
        this.loading = false;
        this.status = 'Ready. Tap "Uygulamayı Aç" to open the app.';
        this.log('token (this is what the app receives)', token);
        this.log('deep link', this.deepLink(token));
        return;
      }

      this.goApp();
    },

    openAppByButton() {
      if (Date.now() - this.tokenIssuedAt < TOKEN_REFRESH_AFTER_MS) {
        this.goApp();
        return;
      }
      // Token süresi dolmak üzere: taze token alıp geçilir (kullanıcı artık mevcut, check yeterli)
      this.loading = true;
      this.status = 'Lütfen bekleyiniz, uygulamaya yönlendiriliyorsunuz.';
      this.ueslmsCheckUser(() => this.goApp());
    },

    deepLink(token) {
      const encodedToken = encodeURIComponent(token);
      const scheme = this.useDevApp ? `${this.config.scheme}-dev` : this.config.scheme;

      if (/Android/i.test(navigator.userAgent)) {
        const androidPackage = this.useDevApp ? `${this.config.androidPackage}.dev` : this.config.androidPackage;
        const fallback = this.config.storeLinks.googlePlay
            ? `S.browser_fallback_url=${encodeURIComponent(this.config.storeLinks.googlePlay)};`
            : '';
        return `intent://login?token=${encodedToken}#Intent;scheme=${scheme};package=${androidPackage};${fallback}end`;
      }
      return `${scheme}://login?token=${encodedToken}`;
    },

    // Android'de çıplak scheme yerine Intent URL: user-activation kısıtlamasına daha az takılır,
    // app yüklü değilse mağazaya düşer. Ayrıntı: KidzwonderJr.vue goApp() notu.
    goApp() {
      this.loading = false;
      this.status = 'Uygulamaya yönlendiriliyorsunuz…';

      // Sekme app'e geçince (visibilitychange) fallback mesajı iptal edilir;
      // birkaç saniye sonra hâlâ buradaysak app muhtemelen açılmadı.
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      clearTimeout(this.appNotOpenedTimer);
      this.appNotOpenedTimer = setTimeout(() => {
        this.appNotOpened = true;
      }, 2500);

      location.href = this.deepLink(this.pendingToken);
    },

    onVisibilityChange() {
      if (document.hidden) {
        clearTimeout(this.appNotOpenedTimer);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
      }
    },
  }
}
</script>

<style scoped>
.app-logo {
  width: 120px;
  height: 120px;
  border-radius: 26px;
}

.divider {
  max-width: 420px;
  margin: 24px auto 0;
  border: none;
  border-top: 2px solid #3E4EF0;
  opacity: 0.3;
}

.open-app-button {
  margin-top: 20px;
  padding: 12px 28px;
  border: none;
  border-radius: 8px;
  background-color: #3E4EF0;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
}

.open-app-button:active {
  opacity: 0.85;
}

.fallback-message {
  max-width: 520px;
  margin: 32px auto 0;
  color: #3E4EF0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.5;
  border-top: 2px solid #3E4EF0;
  padding-top: 15px;
}

.store-badges {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 32px;
}

.store-badges img {
  height: 44px;
  width: auto;
}

.be-debug {
  max-width: 900px;
  margin: 20px auto;
  text-align: left;
  font-family: monospace;
  font-size: 13px;
}

.be-debug-step {
  margin-bottom: 12px;
}

.be-debug-title {
  font-weight: bold;
  margin-bottom: 4px;
}

.be-debug-title.is-ok {
  color: #1b7f3b;
}

.be-debug-title.is-fail {
  color: #c0261a;
}

.be-debug pre {
  background: #f5f5f5;
  border: 1px solid #ddd;
  padding: 10px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
